/**
 * Check if the current device is iOS (iPhone, iPad, iPod)
 * @returns boolean indicating if device is iOS
 */
const checkIfIOS = (): boolean => /iPhone|iPad|iPod/i.test(navigator.userAgent);

/**
 * Check if the current device is Android
 * @returns boolean indicating if device is Android
 */
const checkIfAndroid = (): boolean => /android/i.test(navigator.userAgent);

/**
 * Check if the current browser is Safari
 * @returns boolean indicating if browser is Safari
 */
const checkIfSafari = (): boolean => {
  const ua = navigator.userAgent.toLowerCase();
  return (
    ua.includes("safari") &&
    !ua.includes("chrome") &&
    !ua.includes("crios") &&
    !ua.includes("fxios") &&
    !ua.includes("android")
  );
};

/**
 * Check if the app is running as a standalone PWA
 * @returns boolean indicating if app is in standalone mode
 */
const checkIfStandalone = (): boolean =>
  window.matchMedia("(display-mode: standalone)").matches ||
  (window.navigator as any).standalone || // iOS Safari
  document.referrer.includes("android-app://");

/**
 * Check if the current device is a mobile device
 * Uses multiple detection methods to distinguish actual mobile devices from desktop browsers
 * @returns boolean indicating if device is mobile
 */
const checkIfMobileDevice = (): boolean => {
  const userAgent = navigator.userAgent;

  // Check for desktop OS in user agent FIRST (DevTools often changes UA but keeps some desktop indicators)
  const hasDesktopOS = /Macintosh|Windows|Linux x86_64|X11/i.test(userAgent);

  // If desktop OS is detected, it's not mobile (even if UA says iPhone/Android - it's simulator)
  if (hasDesktopOS) {
    return false;
  }

  // Use modern userAgentData API if available (not fooled by DevTools UA override)
  const userAgentData = (navigator as any).userAgentData;
  if (userAgentData?.mobile !== undefined) {
    return userAgentData.mobile;
  }

  // Finally check user agent for mobile indicators
  const isIOS = checkIfIOS();
  const isAndroid = checkIfAndroid();

  return isIOS || isAndroid;
};

/**
 * Hook to detect mobile devices (Android, iPhone, iPad, iPod) and browser/app states
 * Uses multiple detection methods to distinguish actual mobile devices from desktop browsers
 * @returns object containing device detection flags
 */
const useDeviceDetection = () => {
  const isMobileDevice = checkIfMobileDevice();
  const isIOS = checkIfIOS();
  const isAndroid = checkIfAndroid();
  const isSafari = checkIfSafari();
  const isStandalone = checkIfStandalone();

  return {
    isMobileDevice,
    isIOS,
    isAndroid,
    isSafari,
    isStandalone,
  };
};

export {
  useDeviceDetection,
  checkIfMobileDevice,
  checkIfIOS,
  checkIfAndroid,
  checkIfSafari,
  checkIfStandalone,
};
